import React, { useContext, useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { v4 as uuidv4 } from "uuid"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useSearch } from "hooks/useSearch"
import { useParams } from "react-router-dom"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import Budget2023Context from "context/budget2023/Budget2023Context"
import ListPartida from "./ListPartida"
import i18n from "i18n"
import manualPart from "assets/images/budgeting/manualPart.png"
import cypeLogo from "assets/images/logos/cypeSelected.png"
import preocLogo from "assets/customIcons/preocVert.svg"
import bedecLogo from "assets/images/logos/bedecSelected.png"
import dbLogo from "assets/customIcons/selectedUB.svg"
import tribbooLogo from "assets/images/logos/tribbooSelected.png"
import AuthContext from "context/auth/authContext"
import ModalEditarPartida from "./ModalEditarPartida"
import ModalDeletePart from "./ModalDeletePart"
import CLIENT from "../options/margin/FormulasTab"
import DialogCertificateMeasures from "./DialogCertificacionMeasures/DialogCertificateMeasures"
import ModalCertAbove100 from "../modals/ModalCertAbove100"
import ModalCertChapterConfirm from "../modals/ModalCertChapterConfirm"
import { Tooltip } from "@mui/material"
import CheckboxCert from "./CheckboxCert"

import {
  UNITS,
  TOTALS,
  MARGINS,
  REDUCTIONS,
  MEASUREMENTS,
} from "constants/decimals"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"

import { getCurrency, checkIfMeasuresAreEmpty } from "utils/functions"
import {
  costRealCalc,
  costRealTotalCalc,
  getMargin,
  calcMargin,
  underObjective,
  overObjective,
  overPrice,
  calcApplyDiscount,
  calcularMargenRealPorcentaje,
  calculateTotalWithDiscount,
  getCertViewStateFromPartida,
} from "utils/partUtils"
import ModalDecomposition from "./ModalDecomposition"
import { isNil } from "lodash"
import { isEmpty } from "lodash"
import { PriceInput } from "common/inputs"
import StrikethroughRow from "./StrikethroughRow"
import { PartTableInput } from "components/Part/PartTableInput"
import { useWrapperModals } from "context/wrapperModals/WrapperModalsContext"
import useBudgetConfig from "hooks/budgeting/useBudgetConfig"
import {
  CERTIFY_ACTION,
  getStoredDecision,
  saveDecision,
} from "../utils/certificationDecision"
import { useSnackbar } from "notistack"

const Partida = ({
  certificaciones,
  partidaInfo,
  indice,
  soyFinal,
  projectInfo,
  modificado,
  numeroIndex,
  setModificado,
  contadorActualizaciones,
  setContadorActualizaciones,
  ocultoFinal,
  isChecked,
  setIsChecked,
  version,
  openModalCreatePartida,
  setOpenModalCreatePartida,
  currentPercentageChapter,
  setCurrentPercentageChapter,
  currentPercentagePartida,
  setParentUpdate,
  parentUpdate,
  isFirstTime,
  isDraggingPartida,
  budgetSearchFilterActive = false,
  sequentialModals,
}) => {
  const isCertified = Number(partidaInfo?.cert_previous ?? 0) > 0

  const { projectId } = useParams()
  const { itemsMemo, setItems, queryPartida } = useSearch()
  const [budgetConfig] = useBudgetConfig()
  const preferencias = budgetConfig.preferences

  const {
    formatValue,
    getDecimals,
    roundToDecimals,
    getTotalFromUnit,
    getUnitFromTotal,
    getUnitWithMargin,
    getReductionFromUnit,
    getPriceWithCostMargin,
    getPriceWithClientMargin,
  } = useDecimalFormatter()

  const {
    updatePartidav2,
    updateCertCurrent,
    clearCertification,
    setCertDraftPartidaIncluded,
    checkPartida,
    closeBudget,
    isUpdates,
    budgetTree,
    updatePartsList,
    partsChecked,
    imChecked,
    checkMultipleParts,
    deletePartDecomposition,
    isBudgetClosed,
    certification,
    certDraftPartidaIds,
    marginModal,
    budgetMode,
    baseCert,
  } = useContext(Budget2023Context)

  const isTender = budgetMode === "tender"
  const isBaseCertClient = baseCert === "client"

  const { enqueueSnackbar } = useSnackbar()
  const { newModalPart } = useWrapperModals()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: partidaInfo.partidaId,
    disabled: isBudgetClosed || certificaciones || budgetSearchFilterActive,
  })

  const isBaseCertInitialized = useRef(false)

  useEffect(() => {
    if (!isBaseCertInitialized.current) {
      isBaseCertInitialized.current = true
      return
    }

    const certCurrentPriceView =
      partidaInfo.cert_current *
      (isBaseCertClient ? partidaInfo.clientPrice : partidaInfo.price)

    setViewState({
      ...viewState,
      cert_currentPriceView: certCurrentPriceView,
    })
  }, [baseCert])

  const openModal = isOpen => {
    newModalPart.setProperty({
      open: isOpen,
      chapterId: partidaInfo.chapterId,
      partida: {
        ...partidaInfo,
        ref: `${numeroIndex}.${partidaInfo.position + 1}`,
      },
    })
  }

  const [partida, setPartida] = useState({
    unity: "",
    quantity: 0,
    resumen: "",
    price: 0, // Price should be a number to use fixed decimals
    precioRef: "",
    total: "",
    expenseImputations: partidaInfo.expenseImputations ?? [],
  })

  const [isCertificateMeasuresOpen, setIsCertificateMeasuresOpen] =
    useState(false)
  const [isModalCertAbove100Open, setIsModalCertAbove100Open] = useState(false)
  const [pendingCertValue, setPendingCertValue] = useState(null)
  const [pendingCertType, setPendingCertType] = useState(null)
  const [previousViewState, setPreviousViewState] = useState(null)
  const [isFromParentUpdate, setIsFromParentUpdate] = useState(false)
  const [isCertifying, setIsCertifying] = useState(false)
  const [certifyingAction, setCertifyingAction] = useState(null)
  const [isModalCertChapterConfirmOpen, setIsModalCertChapterConfirmOpen] =
    useState(false)
  const [pendingChapterCertValue, setPendingChapterCertValue] = useState(null)
  const [pendingChapterCertType, setPendingChapterCertType] = useState(null)
  const previousChapterCertValueRef = useRef(null)
  const previousTotalChapterCertValueRef = useRef(null)

  const showClientPrice = certificaciones
    ? isBaseCertClient
    : preferencias.clientPrice && !soyFinal

  const showCostPrice = !certificaciones
    ? preferencias.precioCoste
    : !isBaseCertClient

  const [viewState, setViewState] = useState({})
  const [openEliminate, setOpenEliminate] = useState(false)
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(false)
  const [contadorManual, setContadorManual] = useState(0)
  const [cert_current_percentage, setCert_current_percentage] = useState(0)
  const inputRef = useRef(null)

  // Calculado como (A + P) * precio - P * precio para evitar diferencias por redondeo
  const price = isBaseCertClient ? partida.clientPrice : partida.price
  const certCurrentPriceView =
    viewState.cert_currentPriceView ??
    (partida.cert_current + partida.cert_previous) * price -
      partida.cert_previous * price

  useEffect(() => {
    if (
      currentPercentageChapter < cert_current_percentage ||
      isNil(cert_current_percentage) ||
      isNaN(cert_current_percentage) ||
      isEmpty(cert_current_percentage)
    ) {
      setCert_current_percentage(currentPercentageChapter)
    }
  }, [currentPercentageChapter])

  useEffect(() => {
    for (const [key, value] of Object.entries(parentUpdate)) {
      // Note: Set to currentPercentageUpdate(value, false) to avoid pushing the update (calling the backend)
      // however, right now, we need it to trigger the sum upwards
      if (key === "cert_current_perc") {
        if (!canCertificatePart()) {
          continue
        }
        setIsFromParentUpdate(true)
        currentPercentageUpdate(value, false, true)
      }
      if (key === "cert_total_perc") {
        if (!canCertificateTotalPart()) {
          continue
        }
        setIsFromParentUpdate(true)
        totalPercentageUpdate(parseFloat(value), false, true)
      }
    }
  }, [parentUpdate])

  const onClickStopPropagation = e => {
    if (!e) var e = window.event
    e.cancelBubble = true
    if (e.stopPropagation) e.stopPropagation()
  }

  useEffect(() => {
    if (partidaInfo) {
      setPartida({
        ...partidaInfo,
        name:
          partidaInfo.name == "" ? partidaInfo.description : partidaInfo.name,
        resumen: partidaInfo.description,
        code: partidaInfo.code ? partidaInfo.code : null,
        projectId: projectId,
        total: getTotalFromUnit(partidaInfo.priceReduced, partidaInfo.quantity),
        discount: partidaInfo.discount ? partidaInfo.discount : 0,
        cert_previous: partidaInfo.cert_previous
          ? partidaInfo.cert_previous
          : 0,
        cert_current: partidaInfo.cert_current ? partidaInfo.cert_current : 0,
        costReduction: partidaInfo.costReduction,
        priceReduced: partidaInfo.priceReduced,
        totalWithDiscount: calculateTotalWithDiscount(
          partidaInfo.priceReduced,
          partidaInfo.quantity,
          partidaInfo.discount,
        ),
      })

      const certCurrentPercentageValue = parseFloat(
        (partidaInfo.cert_current / partidaInfo.quantity) * 100,
      ).toFixed(2)
      setCert_current_percentage(certCurrentPercentageValue)
    }
  }, [
    partidaInfo,
    partidaInfo.name,
    partidaInfo.quantity,
    partidaInfo.description,
    partidaInfo.price,
    partidaInfo.priceReduced,
    partidaInfo.unity,
    partidaInfo.discount,
    partidaInfo.clientPrice, // Why do we need to specify each property?
    partidaInfo.clientPriceWithoutDiscount, // doesn't partidaInfo suffice?
    partidaInfo.costReal,
    partidaInfo.expenseImputations,
  ])

  useEffect(() => {
    if (
      partidaInfo.elementInfo == null &&
      partidaInfo.name != "" &&
      partidaInfo.description != ""
    ) {
      //significa que la he creado manual
      styleUpdated.current.id = ""
    } else {
      //NO SOY FINAL
      styleUpdated.current.id = ""
    }

    console.log("[partida]", partida)
  }, [partida, contadorManual, budgetTree.warning])

  useEffect(() => {
    if (partidaInfo) {
      setViewState({})
    }
  }, [partidaInfo])

  const { quantity } = partida

  const [sumMargins, setSumMargins] = useState(0)

  useEffect(() => {
    if (projectInfo) {
      if (projectInfo.industrialProfit) {
        setSumMargins(
          projectInfo.industrialProfit +
            projectInfo.costeGeneral +
            projectInfo.costeIndirecto,
        )
      } else {
        setSumMargins(projectInfo.costeGeneral)
      }
    }
  }, [projectInfo])
  const handleChangePartida = e => {
    setPartida({
      ...partida,
      [e.target.name]: e.target.value,
    })

    if (
      e.target.name == "name" &&
      e.target.value != "" &&
      e.target.value.length >= 3 &&
      !custom
    ) {
      setItems(queryPartida(e.target.value))
    }
    if (e.target.name == "quantity" && e.target.value != "") {
      if (e.target.value < 0) {
        setPartida({
          ...partida,
          quantity: e.target.value * -1,
          total: Math.floor(partida.priceReduced * e.target.value * -100) / 100,
        })
      } else {
        setPartida({
          ...partida,
          quantity: e.target.value,
          total: Math.floor(partida.priceReduced * e.target.value * 100) / 100,
        })
      }
    }
  }

  const { user, getUser, freeTrialExpired, openFreeTrialExpire } =
    useContext(AuthContext)
  useEffect(() => {
    if (!user) {
      getUser()
    }
  }, [])

  const addPartida = item => {
    let wasEmpty = partidaInfo.empty
    setPartida({
      ...partida,
      code: item.id,
      unity: item.unity,
      resumen: item.resumen,
      name: item.name,
      price: roundToDecimals(item.price, UNITS),
    })

    updatePartidav2(
      {
        ...partida,
        name: item.name,
        code: item.id,
        unity: item.unity,
        resumen: item.resumen,
        price: roundToDecimals(item.price, UNITS),
      },
      partidaInfo.chapterId,
      user.sub,
    )
    setItems([])
    setTimeout(() => {
      if (wasEmpty == true && modificado == 1) {
        closeBudget()
      }
    }, 1500)
  }
  const handleCheckBox = e => {
    if (e.nativeEvent.shiftKey) {
      if (partidaInfo.checked) {
        checkPartida(partidaInfo.chapterId, partidaInfo.partidaId)
        updatePartsList(partidaInfo)
      } else {
        checkMultipleParts(partidaInfo.chapterId, partidaInfo)
      }
    } else {
      checkPartida(partidaInfo.chapterId, partidaInfo.partidaId)
      updatePartsList(partidaInfo)
    }
  }

  const clamp = (value, min, max) => {
    return Math.min(Math.max(value, min), max)
  }

  const applyCertifyAt100 = async () => {
    try {
      const totalAt100 = partida.quantity
      const currentAt100 = totalAt100 - partida.cert_previous

      const res = await updateCertCurrent(partida.partidaId, currentAt100)
      if (!res) {
        return
      }

      const newViewstate = {
        cert_totalView: res.cert_total.toFixed(2),
        cert_currentView: res.cert_current.toFixed(2),
        cert_currentPriceView: res.money_current.toFixed(2),
        cert_totalPercView: "100.00",
        cert_currentPercView: parseFloat(
          (100 * res.cert_current) / partida.quantity,
        ).toFixed(2),
      }

      setPartida({
        ...partida,
        cert_current: res.cert_current,
      })

      updateViewState(newViewstate)
    } catch (error) {
      enqueueSnackbar(i18n.t("partida.errorCertifyingPartida"), {
        variant: "error",
      })
    }
  }

  const runCertUpdateWithRollback = async ({
    previousViewState,
    previousPartida,
    callUpdate,
  }) => {
    const trackChapterCert =
      !!certification?.chapterCertInProgress ||
      !!certification?.isChapterCertBatchActive?.()
    if (trackChapterCert) {
      certification.notifyChapterCertApiStarted()
    }
    try {
      const res = await callUpdate()
      if (!res) {
        setPartida(previousPartida)
        updateViewState(previousViewState)
        return false
      }
      return true
    } catch (err) {
      setPartida(previousPartida)
      updateViewState(previousViewState)
      enqueueSnackbar("Error al certificar partida", { variant: "error" })
      return false
    } finally {
      if (!trackChapterCert) return
      certification.notifyChapterCertApiCompleted()
    }
  }

  const openCertAbove100Modal = (certType, value) => {
    setPendingCertValue(value)
    setPendingCertType(certType)
    setPreviousViewState({ ...viewState })
    setIsModalCertAbove100Open(true)
  }

  const createCertAbove100SequentialModalCallbacks = ({
    certType,
    value,
    applyWithStoredDecision,
  }) => {
    const openModalCallback = () => {
      const stored = getStoredDecision(projectId)
      if (
        stored &&
        (stored.action === CERTIFY_ACTION.WITH_VALUE ||
          stored.action === CERTIFY_ACTION.AT_100)
      ) {
        sequentialModals.resolveCurrent(stored.action)
        return
      }
      openCertAbove100Modal(certType, value)
    }

    const resolveModalCallback = async result => {
      if (result === CERTIFY_ACTION.WITH_VALUE) {
        await applyWithStoredDecision()
      } else if (result === CERTIFY_ACTION.AT_100) {
        await applyCertifyAt100()
      }
      setIsFromParentUpdate(false)
    }

    return { openModalCallback, resolveModalCallback }
  }

  const totalCertUpdate = async (value, skipClamp = false) => {
    if (
      !skipClamp &&
      (value > partida.quantity || (100 * value) / partida.quantity > 100)
    ) {
      const storedDecision = getStoredDecision(projectId)
      if (storedDecision) {
        if (storedDecision.action === CERTIFY_ACTION.WITH_VALUE) {
          await totalCertUpdate(value, true)
        } else if (storedDecision.action === CERTIFY_ACTION.AT_100) {
          await applyCertifyAt100()
        }
        return
      }

      setPendingCertValue(value)
      setPendingCertType("totalCert")
      setPreviousViewState({ ...viewState })
      setIsModalCertAbove100Open(true)
      return
    }

    const clamped = skipClamp
      ? value
      : clamp(value, partida.cert_previous, partida.quantity)
    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousCertCurrent = partida.cert_current

    const optimisticViewstate = {
      ...getCertViewStateFromPartida(
        partida,
        clamped - partida.cert_previous,
        price,
      ),
      cert_totalView: clamped.toFixed(2),
      cert_totalPercView: parseFloat(
        (100 * clamped) / partida.quantity,
      ).toFixed(2),
    }
    setPartida({ ...partida, cert_current: clamped - partida.cert_previous })
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida: { ...partida, cert_current: previousCertCurrent },
      callUpdate: () =>
        updateCertCurrent(partida.partidaId, clamped - partida.cert_previous),
    })
  }

  const totalPercentageUpdate = async (
    value,
    skipClamp = false,
    fromParentUpdate = false,
  ) => {
    value = parseFloat(value)

    if (!skipClamp && value > 100) {
      const storedDecision = getStoredDecision(projectId)
      if (storedDecision) {
        if (storedDecision.action === CERTIFY_ACTION.WITH_VALUE) {
          await totalPercentageUpdate(value, true, fromParentUpdate)
        } else if (storedDecision.action === CERTIFY_ACTION.AT_100) {
          await applyCertifyAt100()
        }
        setIsFromParentUpdate(false)
        return
      }

      if ((fromParentUpdate || isFromParentUpdate) && sequentialModals) {
        const modalData = {
          partidaId: partida.partidaId,
          partidaIndex: indice,
          value,
          type: "totalPercentage",
          percentage: value,
        }

        const { openModalCallback, resolveModalCallback } =
          createCertAbove100SequentialModalCallbacks({
            certType: "totalPercentage",
            value,
            applyWithStoredDecision: () =>
              totalPercentageUpdate(value, true, false),
          })

        sequentialModals.enqueue(
          modalData,
          resolveModalCallback,
          openModalCallback,
        )
        return
      }

      openCertAbove100Modal("totalPercentage", value)
      return
    }

    const clamped = skipClamp
      ? value
      : clamp(value, (100 * partida.cert_previous) / partida.quantity, 100)
    const previous_value = parseFloat(
      (100 * (partida.cert_current + partida.cert_previous)) / partida.quantity,
    )
    if (
      !skipClamp &&
      value.toFixed(2) == clamped.toFixed(2) &&
      clamped.toFixed(2) == previous_value.toFixed(2)
    )
      return

    const total = parseFloat((partida.quantity * clamped) / 100)
    const current = total - partida.cert_previous
    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousCertCurrent = partida.cert_current

    const optimisticViewstate = getCertViewStateFromPartida(
      partida,
      current,
      price,
    )
    optimisticViewstate.cert_totalPercView = clamped
    setPartida({ ...partida, cert_current: current })
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida: { ...partida, cert_current: previousCertCurrent },
      callUpdate: () => updateCertCurrent(partida.partidaId, current),
    })
  }

  const currentCertUpdate = async (value, skipClamp = false) => {
    const maxAvailable = partida.quantity - partida.cert_previous
    if (!skipClamp && value > maxAvailable) {
      const storedDecision = getStoredDecision(projectId)
      if (storedDecision) {
        if (storedDecision.action === CERTIFY_ACTION.WITH_VALUE) {
          await currentCertUpdate(value, true)
        } else if (storedDecision.action === CERTIFY_ACTION.AT_100) {
          await applyCertifyAt100()
        }
        return
      }

      setPendingCertValue(value)
      setPendingCertType("currentCert")
      setPreviousViewState({ ...viewState })
      setIsModalCertAbove100Open(true)
      return
    }

    const clamped = skipClamp
      ? value
      : clamp(value, 0, partida.quantity - partida.cert_previous)
    const previous_value = parseFloat(partida.cert_current)
    if (
      !skipClamp &&
      value.toFixed(2) == clamped.toFixed(2) &&
      clamped.toFixed(2) == previous_value.toFixed(2)
    )
      return

    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousCertCurrent = partida.cert_current

    const optimisticViewstate = getCertViewStateFromPartida(
      partida,
      clamped,
      price,
    )
    if (clamped === 0 && parseFloat(previousCertCurrent) > 0) {
      setPartida({
        ...partida,
        cert_current: 0,
        cert_total: partida.cert_previous,
        money_current: 0,
      })
    } else {
      setPartida({ ...partida, cert_current: clamped })
    }
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida: { ...partida, cert_current: previousCertCurrent },
      callUpdate: () =>
        clamped === 0 && parseFloat(previousCertCurrent) > 0
          ? clearCertification(partida.partidaId)
          : updateCertCurrent(partida.partidaId, clamped),
    })
  }

  const currentPercentageUpdate = async (
    value,
    skipClamp = false,
    fromParentUpdate = false,
  ) => {
    value = parseFloat(value)
    const maxPercentage = 100 - (100 * partida.cert_previous) / partida.quantity

    if (!skipClamp && value > maxPercentage) {
      const storedDecision = getStoredDecision(projectId)
      if (storedDecision) {
        if (storedDecision.action === CERTIFY_ACTION.WITH_VALUE) {
          await currentPercentageUpdate(value, true, fromParentUpdate)
        } else if (storedDecision.action === CERTIFY_ACTION.AT_100) {
          await applyCertifyAt100()
        }
        setIsFromParentUpdate(false)
        return
      }

      if ((fromParentUpdate || isFromParentUpdate) && sequentialModals) {
        const modalData = {
          partidaId: partida.partidaId,
          partidaIndex: indice,
          value,
          type: "currentPercentage",
          percentage: (100 * partida.cert_previous) / partida.quantity + value,
        }

        const { openModalCallback, resolveModalCallback } =
          createCertAbove100SequentialModalCallbacks({
            certType: "currentPercentage",
            value,
            applyWithStoredDecision: () =>
              currentPercentageUpdate(value, true, false),
          })

        sequentialModals.enqueue(
          modalData,
          resolveModalCallback,
          openModalCallback,
        )
        return
      }
      openCertAbove100Modal("currentPercentage", value)
      setIsFromParentUpdate(false)
      return
    }

    var clamped = skipClamp
      ? value
      : clamp(value, 0, 100 - (100 * partida.cert_previous) / partida.quantity)
    const previous_value = parseFloat(
      (100 * partida.cert_current) / partida.quantity,
    )
    if (
      !skipClamp &&
      value.toFixed(2) == clamped.toFixed(2) &&
      clamped.toFixed(2) == previous_value.toFixed(2)
    )
      return

    const current = parseFloat((partida.quantity * clamped) / 100)
    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousCertCurrent = partida.cert_current

    const optimisticViewstate = getCertViewStateFromPartida(
      partida,
      current,
      price,
    )
    optimisticViewstate.cert_currentPercView = clamped.toFixed(2)
    optimisticViewstate.cert_totalPercView = parseFloat(
      (parseFloat(partida.cert_previous + current) / partida.quantity) * 100,
    ).toFixed(2)
    if (current === 0 && parseFloat(previousCertCurrent) > 0) {
      setPartida({
        ...partida,
        cert_current: 0,
        cert_total: partida.cert_previous,
        money_current: 0,
      })
    } else {
      setPartida({ ...partida, cert_current: current })
    }
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida: { ...partida, cert_current: previousCertCurrent },
      callUpdate: () =>
        current === 0 && parseFloat(previousCertCurrent) > 0
          ? clearCertification(partida.partidaId)
          : updateCertCurrent(partida.partidaId, current),
    })
    setIsFromParentUpdate(false)
  }

  const handleCertifyWithValue = async (rememberForOneHour = false) => {
    if (pendingCertValue === null || pendingCertType === null) {
      setIsModalCertAbove100Open(false)
      setPendingCertValue(null)
      setPendingCertType(null)
      if (sequentialModals && sequentialModals.hasCurrentModal()) {
        sequentialModals.resolveCurrent(CERTIFY_ACTION.WITH_VALUE)
      }
      return
    }

    setIsCertifying(true)
    setCertifyingAction(CERTIFY_ACTION.WITH_VALUE)
    try {
      if (rememberForOneHour && projectId) {
        saveDecision(projectId, CERTIFY_ACTION.WITH_VALUE)
      }

      const value = parseFloat(pendingCertValue)

      switch (pendingCertType) {
        case "totalCert":
          await totalCertUpdate(value, true)
          break
        case "totalPercentage":
          await totalPercentageUpdate(value, true)
          break
        case "currentCert":
          await currentCertUpdate(value, true)
          break
        case "currentPercentage":
          await currentPercentageUpdate(value, true, false)
          break
      }
    } catch (error) {
      enqueueSnackbar("Error al certificar partida", { variant: "error" })
    } finally {
      setIsCertifying(false)
      setCertifyingAction(null)
      setIsModalCertAbove100Open(false)
      setPendingCertValue(null)
      setPendingCertType(null)
      setPreviousViewState(null)

      if (sequentialModals && sequentialModals.hasCurrentModal()) {
        sequentialModals.resolveCurrent(CERTIFY_ACTION.WITH_VALUE)
      }
    }
  }

  const handleCertifyAt100 = async (rememberForOneHour = false) => {
    if (pendingCertType === null) {
      setIsModalCertAbove100Open(false)
      setPendingCertValue(null)
      setPendingCertType(null)
      if (sequentialModals && sequentialModals.hasCurrentModal()) {
        sequentialModals.resolveCurrent(CERTIFY_ACTION.AT_100)
      }
      return
    }

    setIsCertifying(true)
    setCertifyingAction(CERTIFY_ACTION.AT_100)
    try {
      if (rememberForOneHour && projectId) {
        saveDecision(projectId, CERTIFY_ACTION.AT_100)
      }

      const totalAt100 = partida.quantity
      const currentAt100 = totalAt100 - partida.cert_previous

      const res = await updateCertCurrent(partida.partidaId, currentAt100)
      if (!res) {
        return
      }

      const newViewstate = {
        cert_totalView: res.cert_total.toFixed(2),
        cert_currentView: res.cert_current.toFixed(2),
        cert_currentPriceView: res.money_current.toFixed(2),
        cert_totalPercView: "100.00",
        cert_currentPercView: parseFloat(
          (100 * res.cert_current) / partida.quantity,
        ).toFixed(2),
      }

      setPartida({
        ...partida,
        cert_current: res.cert_current,
      })

      updateViewState(newViewstate)
    } catch (error) {
      enqueueSnackbar("Error al certificar partida", { variant: "error" })
    } finally {
      setIsCertifying(false)
      setCertifyingAction(null)
      setIsModalCertAbove100Open(false)
      setPendingCertValue(null)
      setPendingCertType(null)
      setPreviousViewState(null)

      if (sequentialModals && sequentialModals.hasCurrentModal()) {
        sequentialModals.resolveCurrent(CERTIFY_ACTION.AT_100)
      }
    }
  }

  const certCurrentEffective =
    parseFloat(
      viewState.hasOwnProperty("cert_currentView")
        ? viewState.cert_currentView
        : partida.cert_current || 0,
    ) || 0

  const isGreenCert =
    partida.cert_previous + certCurrentEffective >= partida.quantity

  const isYellowCert =
    (certCurrentEffective > 0 || partida.cert_previous > 0) &&
    partida.cert_previous + certCurrentEffective < partida.quantity

  const hasMeasureCertCurrent =
    partida.measures?.some(m => (parseFloat(m.cert_current) || 0) > 0) ?? false

  const certDraftEntry = certDraftPartidaIds?.[partida.partidaId]
  const isMeasuresCertDraft = certDraftEntry === "measures"
  const isTotalCertDraft = certDraftEntry === "total" || certDraftEntry === true
  const hasCertDraft = !!certDraftEntry

  const measuresCertCheckboxChecked =
    !!partidaInfo.measures?.length &&
    (hasMeasureCertCurrent || hasCertDraft || certCurrentEffective > 0)

  const partidaCertCheckboxLockedToMeasures =
    !!partidaInfo.measures?.length &&
    (hasMeasureCertCurrent || isMeasuresCertDraft)

  const canUncheckPartidaCertCheckbox =
    certCurrentEffective > 0 ||
    (hasCertDraft && !partidaCertCheckboxLockedToMeasures)

  const measureFooterCertCheckboxDisabled =
    !!certification?.loading ||
    (!!partidaInfo.measures?.length &&
      !hasMeasureCertCurrent &&
      !isMeasuresCertDraft &&
      (certCurrentEffective > 0 || isTotalCertDraft))

  const handleMeasureCertCheckboxChange = async checked => {
    if (!partidaInfo.measures?.length) return
    const CERT_CURRENT_DRAFT_EPS = 1e-15
    const measuresList = partida.measures ?? partidaInfo.measures ?? []
    const zeroedMeasures = measuresList.map(m => ({ ...m, cert_current: 0 }))

    if (!checked) {
      const previousViewState = getCertViewStateFromPartida(
        partida,
        partida.cert_current,
        price,
      )
      const previousPartida = { ...partida }
      setPartida({
        ...partida,
        cert_current: 0,
        cert_total: partida.cert_previous,
        money_current: 0,
        measures: zeroedMeasures,
      })
      updateViewState(getCertViewStateFromPartida(partida, 0, price))

      await runCertUpdateWithRollback({
        previousViewState,
        previousPartida,
        callUpdate: () => clearCertification(partida.partidaId),
      })
      return
    }

    const hasAnyCurrent = measuresList.some(
      m => (parseFloat(m.cert_current) || 0) > 0,
    )
    if (hasAnyCurrent) return

    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousPartida = { ...partida }
    const optimisticViewstate = {
      ...getCertViewStateFromPartida(partida, 0, price),
      cert_currentView: String(CERT_CURRENT_DRAFT_EPS),
    }
    setPartida({
      ...partida,
      cert_current: 0,
      cert_total: partida.cert_previous,
      money_current: 0,
      measures: zeroedMeasures,
    })
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida,
      callUpdate: () => updateCertCurrent(partida.partidaId, 0, zeroedMeasures),
    })
  }

  const handleCertCheckboxChange = async e => {
    if (e.target.checked) {
      if (certCurrentEffective > 0) {
        return
      }
      const previousViewState = getCertViewStateFromPartida(
        partida,
        partida.cert_current,
        price,
      )
      const previousPartida = { ...partida }

      // 1e-15: valor >0 mínimo en la vista para marcar el checkbox mientras updateCertCurrent envía 0 al API (borrador sin cantidad certificada).
      const CERT_CURRENT_DRAFT_EPS = 1e-15
      const optimisticViewstate = {
        ...getCertViewStateFromPartida(partida, 0, price),
        cert_currentView: String(CERT_CURRENT_DRAFT_EPS),
      }
      setPartida({
        ...partida,
        cert_current: 0,
        cert_total: partida.cert_previous,
        money_current: 0,
      })
      updateViewState(optimisticViewstate)

      const ok = await runCertUpdateWithRollback({
        previousViewState,
        previousPartida,
        callUpdate: () => updateCertCurrent(partida.partidaId, 0),
      })
      if (ok) {
        setCertDraftPartidaIncluded(partida.partidaId, true, "total")
      }
      return
    }
    const prevCert = parseFloat(partida.cert_current) || 0
    if (prevCert <= 0) {
      if (certCurrentEffective > 0 || hasCertDraft) {
        const previousViewState = getCertViewStateFromPartida(
          partida,
          partida.cert_current,
          price,
        )
        const previousPartida = { ...partida }
        const measuresCleared =
          partida.measures?.length > 0
            ? partida.measures.map(m => ({ ...m, cert_current: 0 }))
            : partida.measures
        setPartida({
          ...partida,
          cert_current: 0,
          cert_total: partida.cert_previous,
          money_current: 0,
          ...(measuresCleared ? { measures: measuresCleared } : {}),
        })
        updateViewState(getCertViewStateFromPartida(partida, 0, price))

        await runCertUpdateWithRollback({
          previousViewState,
          previousPartida,
          callUpdate: () => clearCertification(partida.partidaId),
        })
      }
      return
    }

    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      price,
    )
    const previousPartida = { ...partida }

    const optimisticViewstate = getCertViewStateFromPartida(partida, 0, price)

    if (hasMeasureCertCurrent) {
      const zeroedMeasures = (partida.measures || []).map(m => ({
        ...m,
        cert_current: 0,
      }))
      setPartida({
        ...partida,
        cert_current: 0,
        measures: zeroedMeasures,
      })
      updateViewState(optimisticViewstate)

      await runCertUpdateWithRollback({
        previousViewState,
        previousPartida,
        callUpdate: () =>
          updateCertCurrent(partida.partidaId, 0, zeroedMeasures),
      })
      return
    }

    setPartida({ ...partida, cert_current: 0 })
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida,
      callUpdate: () => clearCertification(partida.partidaId),
    })
  }

  const handleViewBlur = (name, value) => {
    value = parseFloat(value)

    if (isNaN(value) || value === null || value === undefined) {
      setViewState({})
      return
    }

    switch (name) {
      case "cert_totalView":
        totalCertUpdate(value)
        break
      case "cert_totalPercView":
        totalPercentageUpdate(value)
        break
      case "cert_currentView":
        currentCertUpdate(value)
        break
      case "cert_currentPercView":
        currentPercentageUpdate(value, false, false)
        break
      case "price":
        priceUpdate(value)
        break
      case "quantity":
        quantityUpdate(value)
        break
      default:
        updateViewState({ [name]: value.toFixed(2) })
    }
  }

  const updatePriceAndClient = (price, costReduction) => {
    let priceReduced = 0

    if (partida.decomposition.length === 0) {
      priceReduced = getReductionFromUnit(price, costReduction)
    } else {
      priceReduced = partida.decomposition.reduce((acc, item) => {
        const itemTotal = getTotalFromUnit(
          item.priceReduced ?? item.price,
          item.quantity,
        )
        return acc + itemTotal
      }, 0)
    }

    const margin = getMargin(partida)
    const clientPriceWithoutDiscount =
      marginModal.formula === CLIENT
        ? getPriceWithClientMargin(priceReduced, margin)
        : getPriceWithCostMargin(priceReduced, margin)

    const clientPrice = getUnitWithMargin(
      clientPriceWithoutDiscount,
      -partida.discount,
    )

    updatePartidaState({
      ...partida,
      price,
      priceReduced,
      costReduction,
      clientPriceWithoutDiscount,
      clientPrice,
    })
  }

  const handleBlurPrice = (name, value) => {
    const price = parseFloat(value)
    if (price === partida.price) return
    updatePriceAndClient(price, partida.costReduction)
  }

  const handleBlurTotalPrice = (name, value) => {
    const price = getUnitFromTotal(value, partida.quantity)
    if (price === partida.price) return
    updatePriceAndClient(price, partida.costReduction)
  }

  const handleCostReduction = (name, value) => {
    const costReduction = parseFloat(value)
    if (costReduction === partida.costReduction) return
    updatePriceAndClient(partida.price, costReduction)
  }

  const handleBlurClientPrice = (name, value) => {
    if (parseFloat(value) === parseFloat(partida.clientPrice)) return

    const clientPriceWithoutDiscount = parseFloat(value)

    const clientPrice = calcApplyDiscount(
      clientPriceWithoutDiscount,
      partida.discount,
    )
    const margin = calcMargin(
      partida.price,
      clientPriceWithoutDiscount,
      marginModal.formula,
    )

    updatePartidaState({
      ...partida,
      clientPriceWithoutDiscount: clientPriceWithoutDiscount,
      clientPrice: clientPrice,
      margin: margin,
    })
  }

  const handleBlurTotalClientPrice = (name, value) => {
    const res = getTotalFromUnit(partida.clientPrice, partida.quantity)
    const resRounded = Math.round(res * 100) / 100
    const valueRounded = Math.round(parseFloat(value) * 100) / 100

    if (valueRounded === resRounded) return

    const clientPriceWithoutDiscount = getUnitFromTotal(value, partida.quantity)

    const clientPrice = calcApplyDiscount(
      clientPriceWithoutDiscount,
      partida.discount,
    )

    const margin = calcMargin(
      partida.price,
      clientPriceWithoutDiscount,
      marginModal.formula,
    )

    updatePartidaState({
      ...partida,
      clientPriceWithoutDiscount: clientPriceWithoutDiscount,
      clientPrice: clientPrice,
      margin: margin,
    })
  }
  const handleViewChange = e => {
    const { name, value } = e.target
    updateViewState({ [name]: value })
  }

  const currencySymbol = getCurrency(user["custom:currency"])

  const currencyFmt = value => {
    return (
      parseFloat(value).toFixed(2) + " " + getCurrency(user["custom:currency"])
    )
  }

  const quantityUpdate = value => {
    value = parseFloat(value)

    updatePartidaState({ quantity: value })
    setViewState({})
  }

  const priceUpdate = value => {
    value = parseFloat(value)

    const margin = parseFloat(partidaInfo.costeGeneral) / 100
    const clientPriceWithoutDiscount = parseFloat(value / (1 - margin))
    const clientPrice = calcApplyDiscount(
      clientPriceWithoutDiscount,
      partidaInfo.discount,
    )

    updatePartidaState({
      price: value,
      clientPriceWithoutDiscount: clientPriceWithoutDiscount,
      clientPrice: clientPrice,
    })

    setViewState({})
  }

  const currentPercentageChapterOnChange = e => {
    var { name, value } = e.target
    const chapterId = name.replace("currentPercentageChapter", "")

    updateViewState({ [chapterId]: { cert_current_perc: value } })
  }

  const currentPercentageChapterOnFocus = () => {
    const chapterId = partidaInfo.chapterId
    const currentValue =
      viewState.hasOwnProperty(chapterId) &&
      viewState[chapterId].hasOwnProperty("cert_current_perc")
        ? viewState[chapterId]["cert_current_perc"]
        : parseFloat(0).toFixed(2)
    previousChapterCertValueRef.current = currentValue
  }

  const currentPercentageChapterOnBlur = e => {
    var { name, value } = e.target
    const chapterId = name.replace("currentPercentageChapter", "")

    value = parseFloat(value)
    const clamped = isNaN(value) ? 0 : Math.max(0, value)
    const previousValue =
      previousChapterCertValueRef.current ?? parseFloat(0).toFixed(2)
    if (clamped.toFixed(2) === parseFloat(previousValue).toFixed(2)) {
      updateViewState({
        [chapterId]: { cert_current_perc: clamped.toFixed(2) },
      })
      return
    }

    certification.setGeneralUpdate(true)

    updateViewState({ [chapterId]: { cert_current_perc: clamped.toFixed(2) } })

    setPendingChapterCertType("current")
    setPendingChapterCertValue(clamped)
    setIsModalCertChapterConfirmOpen(true)
  }

  const handleChapterCertAccept = () => {
    if (pendingChapterCertValue === null) {
      setIsModalCertChapterConfirmOpen(false)
      setPendingChapterCertValue(null)
      setPendingChapterCertType(null)
      return
    }
    const clamped = pendingChapterCertValue
    const type = pendingChapterCertType
    certification.setGeneralUpdate(true)
    certification.setChapterCertInProgress(true)
    if (type === "current") {
      setParentUpdate({ cert_current_perc: clamped.toFixed(2) })
      currentPercentageUpdate(clamped, false, false)
    } else {
      setParentUpdate({ cert_total_perc: clamped.toFixed(2) })
      totalPercentageUpdate(clamped, false)
    }
    setIsModalCertChapterConfirmOpen(false)
    setPendingChapterCertValue(null)
    setPendingChapterCertType(null)
  }

  const handleChapterCertCancel = () => {
    const chapterId = partidaInfo.chapterId
    const type = pendingChapterCertType
    if (type === "current") {
      const previousValue =
        previousChapterCertValueRef.current ?? parseFloat(0).toFixed(2)
      updateViewState({ [chapterId]: { cert_current_perc: previousValue } })
    } else if (type === "total") {
      const previousValue =
        previousTotalChapterCertValueRef.current ?? parseFloat(0).toFixed(2)
      updateViewState({ [chapterId]: { cert_total_perc: previousValue } })
    }
    setIsModalCertChapterConfirmOpen(false)
    setPendingChapterCertValue(null)
    setPendingChapterCertType(null)
  }

  const updateViewState = updateObject => {
    setViewState({
      ...viewState,
      ...updateObject,
    })
  }

  const chapterCertTdStyle = {
    borderLeft: "1px solid rgb(243, 241, 241)",
    minWidth: "64px",
    background: partidaInfo.contradicted_by_id ? "#E4E4E4" : "#F3D66338",
  }
  const chapterCertInputStyle = {
    color: "#414141",
    fontWeight: "400",
    backgroundColor: "transparent",
    WebkitTextFillColor: "#414141",
    height: "35px",
    fontFamily: "Helvetica",
    width: "80%",
    justifyContent: "center",
    textAlign: "center",
    border: "none",
  }
  const chapterCertWrapperStyle = { height: "auto", width: "80px" }

  const updatePartidaState = partidaObject => {
    var shouldUpdate = false
    var changedKeys = {}
    for (const [key, value] of Object.entries(partidaObject)) {
      if (partida[key] != value) {
        shouldUpdate = true
        changedKeys[key] = value
      }
    }

    if (shouldUpdate) {
      setPartida({
        ...partida,
        ...partidaObject,
      })

      if (!certification.generalUpdate) {
        updatePartidav2(
          {
            ...partida,
            ...partidaObject,
          },
          partidaInfo.chapterId,
          partidaInfo.accountId,
        )
      }

      certification.setGeneralUpdate(false)
    }
  }

  function handleCustom(data) {
    setCustom(true)
    setPartida({
      ...partida,
      unidades: data.unidades ? parseInt(data.unidades) : 0,
      quantity: data.unidades ? parseInt(data.unidades) : 0,
      code: data.code ? data.code : uuidv4(),
      unity: data.unity,
      resumen: data.resumen.length > 0 ? data.resumen : "_",
      name: data.name,
      price: parseFloat(data.price),
      precioRef: parseFloat(
        data.refPrice ? data.refPrice : data.precioRef ? data.precioRef : 0,
      ),
      price: parseFloat(data.price),
      total: parseFloat(data.price * data.unidades),
      position: partida.position,
      comeFrom: data.comeFrom,
    })

    setItems([])
    styleUpdated.current.id = ""
    setContadorManual(contadorManual + 1)
    setTimeout(() => {
      updatePartidav2(
        {
          ...data,
          unidades: data.unidades ? parseInt(data.unidades) : 0,
          price: parseFloat(data.price),
        },
        partidaInfo.chapterId,
        partidaInfo.accountId,
        1,
      )
      styleUpdated.current.id = ""
      setContadorManual(contadorManual + 1)
    }, 2000)
  }
  const styleUpdated = useRef(null)
  const [isUpdated2, setIsUpdated] = useState(0)

  useEffect(() => {
    if (isUpdated2 == 0) {
      if (partidaInfo.updates == null) {
        styleUpdated.current.id = ""
      }
    }
  }, [isUpdated2])

  useEffect(() => {
    if (isUpdates == 0) {
      styleUpdated.current.id = ""
    }
  }, [isUpdates])

  const mergeRefs = (...refs) => {
    const filteredRefs = refs.filter(Boolean)
    if (!filteredRefs.length) return null
    if (filteredRefs.length === 1) return filteredRefs[0]
    return inst => {
      for (const ref of filteredRefs) {
        if (typeof ref === "function") {
          ref(inst)
        } else if (ref) {
          ref.current = inst
        }
      }
    }
  }

  useEffect(() => {
    setIsChecked(imChecked)
  }, [imChecked])

  const [openDecompositionModal, setOpenDecompositionModal] = useState(false)
  const canReorderPart =
    !certificaciones &&
    !isBudgetClosed &&
    !budgetSearchFilterActive &&
    !partidaInfo.checked &&
    partsChecked.length === 0

  const sortableWrapperStyle = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, x: 0 } : null,
    ),
    transition,
    opacity: isDragging ? 0.65 : 1,
    position: "relative",
    zIndex: isDragging ? 2 : "auto",
  }

  const renderDragHandle = extraStyle => {
    if (!canReorderPart) {
      return null
    }

    return (
      <Tooltip
        title={isDraggingPartida ? "" : i18n.t("Reordenar partida")}
        disableHoverListener={isDraggingPartida}
        disableFocusListener={isDraggingPartida}
        disableTouchListener={isDraggingPartida}
      >
        <span
          {...attributes}
          {...listeners}
          onClick={onClickStopPropagation}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isDragging ? "grabbing" : "grab",
            color: "#414141",
            touchAction: "none",
            ...extraStyle,
          }}
        >
          <DragIndicatorIcon style={{ fontSize: "18px" }} />
        </span>
      </Tooltip>
    )
  }

  const manageDecomposition = async partidaInfo => {
    if (partidaInfo.decomposition.length > 0) {
      await deletePartDecomposition(partidaInfo, false)
      updatePartidaState({ ...partida, decomposition: [] })
    } else {
      await deletePartDecomposition(partidaInfo, true)
      updatePartidaState({
        ...partida,
        _decomposition: [],
        decomposition: partida._decomposition,
      })
    }
  }

  const getCostRealValue = () => {
    const costRealValue = costRealCalc(partida)
    return costRealValue
  }

  const costRealTotalValue = costRealTotalCalc(partida)

  const saveCostRealValue = (name, value) => {
    const dataToSave = {
      ...partida,
      [name]: parseFloat(value),
    }

    updatePartidaState(dataToSave)
  }

  const onBlurCostRealTotal = (name, value) => {
    const costReal = getUnitFromTotal(value, partida.quantity)

    updatePartidaState({ ...partida, costReal: costReal })
  }

  const costRealAndCostRealTotalView = () => {
    return (
      <>
        <PartTableInput
          disabled={isCertified}
          onClick={onClickStopPropagation}
          name="costReal"
          value={getCostRealValue()}
          symbol={currencySymbol}
          partida={partida}
          onBlur={saveCostRealValue}
          decimals={getDecimals(UNITS)}
        />
        <PartTableInput
          disabled={isCertified}
          onClick={onClickStopPropagation}
          name="costRealTotal"
          symbol={currencySymbol}
          value={costRealTotalValue}
          partida={partida}
          onBlur={onBlurCostRealTotal}
          decimals={getDecimals(TOTALS)}
        />
      </>
    )
  }

  const canCertificateMeasures = () => {
    if (!certificaciones) return false
    if (partidaInfo.measures?.length <= 0) return false
    if (partidaInfo.cert_current === 0 && partidaInfo.cert_previous === 0)
      return true

    const certs = partidaInfo.measures?.some(
      measure => measure.cert_current > 0 || measure.cert_previous > 0,
    )

    if (!certs) return false

    return true
  }

  const canCertificateTotalPart = () => {
    if (partidaInfo.contradicted_by_id) return false
    if (!canCertificatePart()) return false
    return true
  }

  const canCertificatePart = () => {
    if (partidaInfo.contradicted_by_id) return false
    if (partidaInfo.cert_current === 0 && partidaInfo.cert_previous === 0)
      return true

    const certs = partidaInfo.measures?.some(
      measure => measure.cert_current > 0 || measure.cert_previous > 0,
    )
    if (!certs) return true

    return false
  }

  const partidaCertCheckboxChecked =
    certCurrentEffective > 0 || (!!partidaInfo.measures?.length && hasCertDraft)

  const partidaCertCheckboxDisabled =
    !!partidaInfo.contradicted_by_id ||
    certification?.loading ||
    partidaCertCheckboxLockedToMeasures ||
    (!canCertificatePart() && !canUncheckPartidaCertCheckbox)

  const showMeasureCertModalOpener =
    canCertificateMeasures() &&
    !(partidaCertCheckboxChecked && !partidaCertCheckboxDisabled)

  const handleMeasuresUpdate = async measures => {
    const totalCertCurrent = measures.reduce(
      (sum, item) => sum + (parseFloat(item.cert_current) || 0),
      0,
    )
    const previousViewState = getCertViewStateFromPartida(
      partida,
      partida.cert_current,
      partida.clientPrice,
    )
    const previousPartida = { ...partida }
    const previousCertCurrent = partida.cert_current
    const previousMeasures = partida.measures

    const CERT_MEASURES_SUM_ZERO_EPS = 1e-15
    const hadMeasureGridCertOrMeasuresDraft =
      previousMeasures?.some(
        m => (parseFloat(m.cert_current) || 0) > CERT_MEASURES_SUM_ZERO_EPS,
      ) || certDraftPartidaIds?.[partida.partidaId] === "measures"

    if (
      totalCertCurrent <= CERT_MEASURES_SUM_ZERO_EPS &&
      hadMeasureGridCertOrMeasuresDraft
    ) {
      const zeroedMeasures = measures.map(m => ({ ...m, cert_current: 0 }))
      setPartida({
        ...partida,
        cert_current: 0,
        cert_total: partida.cert_previous,
        money_current: 0,
        measures: zeroedMeasures,
      })
      updateViewState(getCertViewStateFromPartida(partida, 0, price))

      await runCertUpdateWithRollback({
        previousViewState,
        previousPartida,
        callUpdate: () => clearCertification(partida.partidaId),
      })
      return
    }

    const optimisticViewstate = {
      cert_totalView: (partidaInfo.cert_previous + totalCertCurrent).toFixed(2),
      cert_currentView: totalCertCurrent.toFixed(2),
      cert_currentPercView: parseFloat(
        (100 * totalCertCurrent) / partidaInfo.quantity,
      ).toFixed(2),
      cert_currentPriceView: parseFloat(
        totalCertCurrent * partidaInfo.clientPrice,
      ).toFixed(2),
      cert_totalPercView: parseFloat(
        (100 * (partidaInfo.cert_previous + totalCertCurrent)) /
          partidaInfo.quantity,
      ).toFixed(2),
    }
    setPartida({ ...partida, cert_current: totalCertCurrent, measures })
    updateViewState(optimisticViewstate)

    await runCertUpdateWithRollback({
      previousViewState,
      previousPartida: {
        ...partida,
        cert_current: previousCertCurrent,
        measures: previousMeasures,
      },
      callUpdate: () =>
        updateCertCurrent(partida.partidaId, totalCertCurrent, measures),
    })
  }

  return (
    <>
      <StrikethroughRow
        ref={mergeRefs(styleUpdated, setNodeRef)}
        $showStrikethrough={Boolean(partidaInfo.contradicted_by_id)}
        className="position-relative"
        style={sortableWrapperStyle}
      >
        {certificaciones && indice == 0 && (
          <tr
            className="partidasInfo"
            style={{
              display: "",
              visibility: "inherit",
              borderBottom: "0px",
              border: partidaInfo.checked ? "1.5px solid #4461D7" : "0px",
            }}
          >
            {
              //CHECKBOX Y UNIDADES
            }
            <td style={{ borderLeft: "none", width: "79px" }}>
              <div
                className="inline"
                style={{
                  height: "35px",
                  textAlign: "center",
                  justifyContent: "center",
                }}
              ></div>
            </td>

            <td style={{ borderLeft: "none" }}>
              <div
                id="estiloBuscador"
                style={{ display: "flex", position: "relative" }}
              >
                <p
                  style={{
                    display: "block",
                    textAlign: "left",
                    marginBottom: "0px",
                    marginRight: "20px",
                    marginLeft: "23px",
                  }}
                >
                  <span
                    style={{ maxWidth: "100% !important", fontWeight: "600" }}
                    className="partidaName"
                    role="textbox"
                    autoComplete="off"
                    name="resumen"
                  >
                    {" "}
                    Certificación del capítulo
                  </span>
                </p>
              </div>
            </td>

            {
              <td
                style={{
                  maxWidth: "86px",
                  borderLeft: "none",
                }}
              ></td>
            }

            <td
              style={{
                minWidth: "48px",
                maxWidth: "64px",
                borderLeft: "none",
              }}
            >
              <div style={{ height: "auto" }}></div>
            </td>

            {
              //AQUI ESTA EL PRECIO OBJETIVO Y SU TOTAL
              preferencias.precioCoste && !certificaciones && (
                <>
                  <td
                    style={{
                      minWidth: "127px",
                      color: "#414141",
                      background:
                        partidaInfo.decomposition.length > 0 && "#E4E4E4",
                      borderLeft: "none",
                    }}
                  >
                    <div style={{ marginTop: "", height: "auto" }}></div>
                  </td>
                  <td
                    style={{
                      minWidth: "126px",
                      color: "#414141",
                      position: "relative",
                      borderLeft: "none",
                    }}
                  >
                    <div style={{ marginTop: "", height: "auto" }}></div>
                  </td>
                </>
              )
            }
            {
              //AQUI ESTA EL COSTE REDUCIDO Y SU TOTAL
              preferencias.costeReducido && !certificaciones && (
                <>
                  <td
                    style={{
                      minWidth: "127px",
                      color: "#414141",
                      background:
                        partidaInfo.decomposition.length > 0 && "#E4E4E4",
                      borderLeft: "none",
                    }}
                  >
                    <div style={{ marginTop: "", height: "auto" }}></div>
                  </td>
                  <td
                    style={{
                      minWidth: "126px",
                      color: "#414141",
                      position: "relative",
                      borderLeft: "none",
                    }}
                  >
                    <div style={{ marginTop: "", height: "auto" }}></div>
                  </td>
                </>
              )
            }
            {certificaciones && (
              <>
                <td style={{ borderLeft: "0px" }}></td>

                <td style={{ borderLeft: "0px" }}></td>
                <td style={{ borderLeft: "0px" }}></td>

                <td style={{ borderLeft: "0px" }}></td>
                <td style={{ borderLeft: "0px" }}></td>
                <td style={{ borderLeft: "0px" }}></td>
                <td style={{ borderLeft: "0px" }}></td>
                {preferencias.basededatos && (
                  <>
                    <td style={{ borderLeft: "0px" }}></td>
                    <td style={{ borderLeft: "0px" }}></td>
                  </>
                )}
                <td style={chapterCertTdStyle}>
                  <div style={chapterCertWrapperStyle}>
                    <PriceInput
                      ref={inputRef}
                      id={`currentPercentageChapter${partida.chapterId}`}
                      name={`currentPercentageChapter${partida.chapterId}`}
                      value={
                        viewState.hasOwnProperty(partida.chapterId) &&
                        viewState[partida.chapterId].hasOwnProperty(
                          "cert_current_perc",
                        )
                          ? viewState[partida.chapterId]["cert_current_perc"]
                          : parseFloat(0).toFixed(2)
                      }
                      onClick={e => {
                        if (!e) var e = window.event
                        e.cancelBubble = true
                        if (e.stopPropagation) e.stopPropagation()
                        inputRef.current && inputRef.current.select()
                      }}
                      onFocus={currentPercentageChapterOnFocus}
                      min="0"
                      type="number"
                      onChange={currentPercentageChapterOnChange}
                      onBlur={currentPercentageChapterOnBlur}
                      style={chapterCertInputStyle}
                      disabled={partidaInfo.contradicted_by_id}
                    />
                  </div>
                </td>
              </>
            )}
            {
              //COLUMNAS DESCUENTO
            }
            {preferencias.descuento && !certificaciones && (
              <td
                style={{
                  borderLeft: "1px solid #F3F1F1",
                  background: "#F3D66338",
                }}
              ></td>
            )}
            {
              //AQUI ESTAN LAS OPCIONES ON HOVER
            }
            {!certificaciones && (
              <td
                style={{
                  borderLeft: "none",
                  minWidth: "40px",
                  height: "35px",
                  cursor: "default",
                  background:
                    preferencias.descuento == true ? "#F3D66338" : null,
                }}
              >
                {!certificaciones && (
                  <div
                    onClick={e => {
                      if (!e) var e = window.event
                      e.cancelBubble = true
                      if (e.stopPropagation) e.stopPropagation()
                    }}
                    className="detallesPartiShow"
                    style={{
                      backgroundColor: "transparent",
                      width: "40px",
                      zIndex: "10",
                      position: "relative",
                      minWidth: "40px",
                      paddingTop: "5px",
                      height: "35px",
                      justifyContent: "center",
                      marginLeft: "-10px",
                      cursor: "default",
                    }}
                  >
                    {partidaInfo.checked ? (
                      <DeleteOutlineIcon
                        style={{
                          color: "#414141",
                          width: "18px",
                          cursor: "pointer",
                        }}
                        onClick={e => {
                          freeTrialExpired
                            ? openFreeTrialExpire()
                            : setOpenEliminate(true)
                        }}
                      />
                    ) : partsChecked.length > 0 ? (
                      <div></div>
                    ) : (
                      <div className="inline">
                        <DeleteOutlineIcon
                          style={{
                            color: "#414141",
                            width: "16px",
                            marginRight: "-3px",
                            cursor: "pointer",
                          }}
                          onClick={e => {
                            freeTrialExpired
                              ? openFreeTrialExpire()
                              : setOpenEliminate(true)
                          }}
                        />
                        {renderDragHandle({ marginLeft: "3px" })}
                      </div>
                    )}
                  </div>
                )}
              </td>
            )}
          </tr>
        )}
        <tr
          onClick={e => {
            if (!e) var e = window.event
            e.cancelBubble = true
            if (e.stopPropagation) e.stopPropagation()
            !certificaciones && openModal(true)
          }}
          className="partidasInfo"
          data-testid="partida-row"
          style={{
            cursor: "pointer",
            display: "",
            visibility: "inherit",
            borderBottom: "0px",
            border: partidaInfo.checked ? "1.5px solid #4461D7" : "0px",
          }}
        >
          {
            //CHECKBOX Y UNIDADES
          }
          <td
            style={{
              borderLeft: "none",
              width: "79px",
              backgroundColor: partidaInfo.comment && "#F5D2D2",
            }}
          >
            <div
              className="inline"
              style={{
                height: "35px",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              {!certificaciones && (
                <input
                  type={"checkbox"}
                  onClick={e => {
                    if (!e) var e = window.event
                    e.cancelBubble = true
                    if (e.stopPropagation) e.stopPropagation()
                  }}
                  onChange={e => {
                    if (!e) var e = window.event
                    e.cancelBubble = true
                    if (e.stopPropagation) e.stopPropagation()
                    handleCheckBox(e)
                  }}
                  checked={partidaInfo.checked}
                  style={{
                    width: "12px",
                    marginRight: "5px",
                    cursor: "pointer",
                    marginTop: "-2px",
                  }}
                ></input>
              )}

              <p
                className="segundoIconoShow"
                style={{
                  paddingTop: "8px",
                  color: partidaInfo.comment ? "#BF2929" : "#414141",
                  fontSize: "14px",
                }}
              >
                {numeroIndex + "." + (partidaInfo.position + 1)}
              </p>
              {partidaInfo.comment && (
                <div
                  className="segundoIcono"
                  style={{
                    position: "absolute",
                    marginLeft: "17px",
                    marginTop: "30px",
                    zIndex: "99",
                  }}
                >
                  <div className="recuadro">
                    <div className="recuadroPico"></div>
                    {i18n.t("Contiene notas")}
                  </div>
                </div>
              )}
            </div>
          </td>
          {
            //COLUMNAS CODE AND DESCRIPCION
          }
          <td style={{ borderLeft: "1px solid #E9ECEF" }}>
            <div style={{ display: "flex", width: "100%", height: "100%" }}>
              <div
                style={{
                  width: "100px",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  paddingLeft: "10px",
                  borderRight: "1px solid #E9ECEF",
                }}
                data-testid="partida-code"
              >
                <span
                  style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {partidaInfo.code}
                </span>
              </div>
              <div style={{ flex: "1", height: "100%" }}>
                <div
                  id="estiloBuscador"
                  style={{
                    display: "flex",
                    position: "relative",
                    height: "100%",
                    width: "-webkit-fill-available",
                    display: "flex",
                    alignItems: "center",
                  }}
                  data-element="partidaName"
                  data-testid="partida-name"
                  onClick={e => {
                    if (!e) var e = window.event
                    e.cancelBubble = true
                    if (e.stopPropagation) e.stopPropagation()
                    !certificaciones && openModal(true)
                  }}
                >
                  {partidaInfo.adjustedPartIds?.length > 0 && (
                    <Tooltip
                      title={i18n.t(
                        "Partida de ajuste creada automaticamente al generar una contradictoria",
                      )}
                      arrow
                    >
                      <div style={{ marginLeft: "5px" }}>⚖️</div>
                    </Tooltip>
                  )}
                  <ListPartida
                    items={itemsMemo}
                    addPartida={addPartida}
                    handleChangePartida={handleChangePartida}
                    partida={partida}
                    handleCustom={handleCustom}
                    estoyEnFinal={soyFinal}
                    creadaEnFinal={partidaInfo.creadaEnFinal}
                    projectInfo={projectInfo}
                    version={version}
                    openModalCreatePartida={openModalCreatePartida}
                    setOpenModalCreatePartida={setOpenModalCreatePartida}
                    setOpenAdd={openModal}
                    setPartida={setPartida}
                  />

                  {preferencias.procedencia &&
                    !certificaciones &&
                    (partidaInfo.comeFrom == user.sub && !soyFinal ? (
                      <div
                        className="editarPartidaDiv"
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          top: "55%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "32px",
                            height: "32px",
                            cursor: "pointer",
                          }}
                          className="segundoIconoShow"
                          onClick={e => {
                            //e.preventDefault(); e.stopPropagation();
                            //setOpen(true)
                          }}
                          src={manualPart}
                        />
                        <div
                          className="segundoIcono"
                          style={{
                            position: "absolute",
                            marginLeft: "-20px",
                            marginTop: "-2px",
                            zIndex: "99",
                          }}
                        >
                          <div className="recuadro">
                            <div className="recuadroPico"></div>
                            {i18n.t("Editar partida")}
                          </div>
                        </div>
                      </div>
                    ) : partidaInfo.comeFrom == "cype" ? (
                      <div
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          height: "100%",
                          justifyContent: "center",
                          display: "flex",
                          top: "47%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "22px",
                            height: "22px",
                            marginLeft: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            position: "absolute",
                          }}
                          src={cypeLogo}
                        />
                      </div>
                    ) : partidaInfo.comeFrom == "bedec" ? (
                      <div
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          height: "100%",
                          justifyContent: "center",
                          display: "flex",
                          top: "47%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "22px",
                            height: "22px",
                            marginLeft: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            position: "absolute",
                          }}
                          src={bedecLogo}
                        />
                      </div>
                    ) : partidaInfo.comeFrom == "tribboo" ? (
                      <div
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          height: "100%",
                          justifyContent: "center",
                          display: "flex",
                          top: "47%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "22px",
                            height: "22px",
                            marginLeft: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            position: "absolute",
                          }}
                          src={tribbooLogo}
                        />
                      </div>
                    ) : partidaInfo.comeFrom == "preoc" ? (
                      <div
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          height: "100%",
                          justifyContent: "center",
                          display: "flex",
                          top: "47%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "22px",
                            height: "22px",
                            marginLeft: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            position: "absolute",
                          }}
                          src={preocLogo}
                        />
                      </div>
                    ) : partidaInfo.comeFrom == "user-database" ? (
                      <div
                        style={{
                          position: "absolute",
                          width: "50px",
                          right: "0px",
                          height: "100%",
                          justifyContent: "center",
                          display: "flex",
                          top: "47%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <img
                          style={{
                            width: "19px",
                            height: "19px",
                            marginLeft: "2px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            position: "absolute",
                          }}
                          src={dbLogo}
                        />
                      </div>
                    ) : null)}

                  {showMeasureCertModalOpener && (
                    <div
                      style={{
                        position: "absolute",
                        marginLeft: "-20px",
                        right: 0,
                      }}
                    >
                      <img
                        style={{
                          width: "32px",
                          height: "32px",
                          cursor: "pointer",
                        }}
                        className="segundoIconoShow"
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          setIsCertificateMeasuresOpen(true)
                        }}
                        src={manualPart}
                      />
                      <div
                        className="segundoIcono"
                        style={{
                          position: "absolute",
                          marginLeft: "-45px",
                          marginTop: "-2px",
                          zIndex: "99",
                        }}
                      >
                        <div className="recuadro">
                          <div className="recuadroPico"></div>
                          {i18n.t("certMeasurements.title")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </td>
          {
            //COLUMNAS CANTIDAD
            quantity == "Unidades" ? (
              <td style={{ minWidth: "86px", borderLeft: "1px solid #E9ECEF" }}>
                <div style={{ height: "auto" }}>{quantity}</div>
              </td>
            ) : (
              <td
                style={{
                  background:
                    (!checkIfMeasuresAreEmpty(partidaInfo.measures) &&
                      new Date(partidaInfo.createdAt).getTime() !==
                        new Date(partidaInfo.updatedAt).getTime()) ||
                    isCertified
                      ? "#E4E4E4"
                      : undefined,
                  minWidth: "86px",
                  borderLeft: "1px solid #E9ECEF",
                  verticalAlign: "middle",
                  padding: 0,
                }}
              >
                <PartTableInput
                  wrapInTd={false}
                  onClick={onClickStopPropagation}
                  partida={partida}
                  value={
                    "quantity" in viewState
                      ? viewState.quantity
                      : partida.quantity
                  }
                  symbol=""
                  name="quantity"
                  decimals={getDecimals(MEASUREMENTS)}
                  onChange={handleViewChange}
                  onBlur={handleViewBlur}
                  disabled={
                    isCertified ||
                    isBudgetClosed ||
                    isTender ||
                    projectInfo.status === "CLOSED" ||
                    version ||
                    certificaciones
                      ? true
                      : user
                        ? freeTrialExpired || user["custom:role"] === "WORKER"
                          ? true
                          : !checkIfMeasuresAreEmpty(partidaInfo.measures)
                            ? true
                            : false
                        : false
                  }
                />
              </td>
            )
          }
          {
            //COLUMNA UNIDAD
            <td
              style={{
                maxWidth: "86px",
                borderLeft: "1px solid #E9ECEF",
              }}
            >
              <div style={{ marginTop: "", height: "auto" }}>
                {partidaInfo.unity}
              </div>
            </td>
          }
          {
            //COLUMNAS REF BD
          }
          {preferencias.basededatos == true && soyFinal != true ? (
            <>
              <td
                colSpan={"2"}
                style={{
                  maxWidth: "86px",

                  borderLeft: "1px solid #E9ECEF",
                }}
              >
                <div style={{ marginTop: "", height: "auto" }}>
                  {formatValue(
                    parseFloat(partidaInfo.costeGeneral) +
                      parseFloat(partidaInfo.costeIndirecto) +
                      parseFloat(partidaInfo.industrialProfit),
                    MARGINS,
                  )}
                  %
                </div>
              </td>
            </>
          ) : null}
          {
            // Costo Objetivo
            showCostPrice && (
              <>
                <PartTableInput
                  disabled={
                    certificaciones ||
                    isCertified ||
                    isBudgetClosed ||
                    isTender ||
                    partida.decomposition?.length > 0
                  }
                  onClick={onClickStopPropagation}
                  name="price"
                  value={partida.price}
                  partida={partida}
                  symbol=""
                  onBlur={handleBlurPrice}
                  decimals={getDecimals(UNITS)}
                />

                <PartTableInput
                  disabled={
                    certificaciones ||
                    isCertified ||
                    isBudgetClosed ||
                    isTender ||
                    partida.decomposition?.length > 0
                  }
                  onClick={onClickStopPropagation}
                  name="priceTotal"
                  value={getTotalFromUnit(partida.price, partida.quantity)}
                  partida={partida}
                  decimals={getDecimals(TOTALS)}
                  symbol=""
                  onBlur={handleBlurTotalPrice}
                  isOutlined={partidaInfo?.decomposition.length > 0}
                  outlined={e => {
                    e.stopPropagation()
                    setOpenDecompositionModal(true)
                  }}
                />
              </>
            )
          }
          {
            // Coste Reducido (porcentaje, por unidad y total)
            preferencias.costeReducido && !certificaciones && (
              <>
                <PartTableInput
                  disabled={isBudgetClosed}
                  onClick={onClickStopPropagation}
                  name="costReduction"
                  value={partida.costReduction}
                  partida={partida}
                  symbol="%"
                  onBlur={handleCostReduction}
                  decimals={getDecimals(REDUCTIONS)}
                />

                <PartTableInput
                  disabled={true}
                  name="priceReduced"
                  value={parseFloat(partida.priceReduced)}
                  partida={partida}
                  symbol=""
                  decimals={getDecimals(UNITS)}
                />

                <PartTableInput
                  disabled={true}
                  name="totalPriceReduced"
                  value={getTotalFromUnit(
                    partida.priceReduced,
                    partida.quantity,
                  )}
                  decimals={getDecimals(TOTALS)}
                  partida={partida}
                  symbol=""
                />
              </>
            )
          }
          {
            // Coste Real
            preferencias.costReal && !certificaciones && (
              <>
                {
                  // Margen Real
                  preferencias.basededatos && (
                    <>
                      <PartTableInput
                        name="marginReal"
                        symbol={"%*"}
                        disabled={true}
                        green={partida.quantity && underObjective(partida)}
                        yellow={partida.quantity && overObjective(partida)}
                        red={partida.quantity && overPrice(partida)}
                        onClick={onClickStopPropagation}
                        value={calcularMargenRealPorcentaje(
                          partida,
                          marginModal.formula,
                        )}
                        decimals={getDecimals(MARGINS)}
                        partida={partida}
                      />
                    </>
                  )
                }

                {costRealAndCostRealTotalView()}
              </>
            )
          }
          {
            // Precio Cliente
            showClientPrice && (
              <>
                <PartTableInput
                  name="clientPriceWithoutDiscount"
                  partida={partida}
                  disabled={isBudgetClosed || certificaciones || isCertified} // add "|| partida.costReal" or "project.is_closed" later
                  onClick={onClickStopPropagation}
                  symbol=""
                  value={
                    partida.hasOwnProperty("clientPriceWithoutDiscount")
                      ? partida.clientPriceWithoutDiscount
                      : null
                  }
                  decimals={getDecimals(UNITS)}
                  onBlur={handleBlurClientPrice}
                />

                <PartTableInput
                  name="clientPriceWithoutDiscountTotal"
                  partida={partida}
                  symbol=""
                  disabled={isBudgetClosed || certificaciones || isCertified} // add "|| partida.costReal" or "project.is_closed" later
                  onClick={onClickStopPropagation}
                  value={
                    partida.hasOwnProperty("clientPriceWithoutDiscount")
                      ? getTotalFromUnit(
                          partida.clientPriceWithoutDiscount,
                          partida.quantity,
                        )
                      : null
                  }
                  decimals={getDecimals(TOTALS)}
                  onBlur={handleBlurTotalClientPrice}
                />
              </>
            )
          }
          {
            // Precio Cliente con Descuento
            preferencias.descuento && !certificaciones && (
              <>
                <PartTableInput
                  name="clientPriceDiscount"
                  partida={partida}
                  symbol={"%"}
                  disabled
                  value={partida.discount}
                  decimals={getDecimals(MARGINS)}
                />

                <PartTableInput
                  name="clientPrice"
                  partida={partida}
                  symbol=""
                  disabled={true}
                  onClick={onClickStopPropagation}
                  decimals={getDecimals(UNITS)}
                  value={
                    partida.hasOwnProperty("clientPrice")
                      ? partida.clientPrice
                      : null
                  } /* partida parece no tener esa propiedad, si partidaInfo */
                />

                <PartTableInput
                  name="clientPrice"
                  partida={partida}
                  symbol=""
                  disabled={true}
                  onClick={onClickStopPropagation}
                  decimals={getDecimals(TOTALS)}
                  value={
                    partida.hasOwnProperty("clientPrice")
                      ? getTotalFromUnit(partida.clientPrice, partida.quantity)
                      : null
                  }
                />
              </>
            )
          }
          {
            // Certificaciones
            certificaciones && (
              <>
                {/* Certificación Total */}
                <PartTableInput
                  name="cert_totalView"
                  partida={viewState}
                  onClick={onClickStopPropagation}
                  symbol={i18n.t("Ud.")}
                  value={
                    viewState.hasOwnProperty("cert_totalView")
                      ? viewState.cert_totalView
                      : parseFloat(partida.cert_current + partida.cert_previous)
                  }
                  decimals={getDecimals(MEASUREMENTS)}
                  onBlur={handleViewBlur}
                  disabled={!canCertificateTotalPart()}
                  green={isGreenCert}
                  yellow={isYellowCert}
                />
                <PartTableInput
                  name="cert_totalPercView"
                  partida={viewState}
                  onClick={onClickStopPropagation}
                  symbol={"%"}
                  value={
                    viewState.hasOwnProperty("cert_totalPercView")
                      ? viewState.cert_totalPercView
                      : Math.max(
                          0,
                          parseFloat(
                            ((partida.cert_previous + partida.cert_current) /
                              partida.quantity) *
                              100 || 0,
                          ),
                        )
                  }
                  decimals={2}
                  onBlur={handleViewBlur}
                  disabled={!canCertificateTotalPart()}
                  green={isGreenCert}
                  yellow={isYellowCert}
                />
                {/* Certificación previa */}
                <PartTableInput
                  name="cert_previous"
                  partida={partida}
                  symbol={i18n.t("Ud.")}
                  disabled={true}
                  value={partida.cert_previous}
                />
                <PartTableInput
                  name="cert-previousPerc"
                  partida={partida}
                  symbol={"%"}
                  disabled={true}
                  decimals={2}
                  value={(partida.cert_previous / partida.quantity) * 100}
                />
                {/* Certificación actual */}
                <PartTableInput
                  name="cert_currentView"
                  partida={viewState}
                  onClick={onClickStopPropagation}
                  symbol={i18n.t("Ud.")}
                  value={
                    viewState.hasOwnProperty("cert_currentView")
                      ? viewState.cert_currentView
                      : parseFloat(partida.cert_current || 0).toFixed(2)
                  }
                  decimals={getDecimals(MEASUREMENTS)}
                  disabled={!canCertificatePart()}
                  onChange={handleViewChange}
                  onBlur={handleViewBlur}
                />
                <PartTableInput
                  name="cert_currentPercView"
                  partida={viewState}
                  onClick={onClickStopPropagation}
                  symbol={"%"}
                  decimals={2}
                  value={
                    viewState.hasOwnProperty("cert_currentPercView")
                      ? viewState.cert_currentPercView
                      : Math.max(
                          0,
                          parseFloat(
                            (100 * partida.cert_current) / partida.quantity ||
                              0,
                          ),
                        ).toFixed(2)
                  }
                  disabled={!canCertificatePart()}
                  onChange={handleViewChange}
                  onBlur={handleViewBlur}
                />
                <PartTableInput
                  name="cert_currentPriceView"
                  partida={viewState}
                  symbol=""
                  decimals={getDecimals(TOTALS)}
                  disabled={true}
                  value={certCurrentPriceView}
                />
              </>
            )
          }
          {
            // Checkbox para certificar la partida
            certificaciones && (
              <td>
                <CheckboxCert
                  checked={partidaCertCheckboxChecked}
                  onChange={handleCertCheckboxChange}
                  disabled={partidaCertCheckboxDisabled}
                />
              </td>
            )
          }
          {
            // Drag handle
            !certificaciones && (
              <td
                style={{
                  borderLeft: "none",
                  minWidth: "35px",
                  height: "35px",
                  cursor: "default",
                  textAlign: "center",
                  verticalAlign: "middle",
                  background:
                    preferencias.descuento == true ? "#F3D66338" : null,
                }}
              >
                <div
                  onClick={onClickStopPropagation}
                  className="detallesPartiShow"
                  style={{
                    backgroundColor: "transparent",
                    width: "fit-content",
                    margin: "0 auto",
                    zIndex: "10",
                    position: "relative",
                    paddingTop: "5px",
                    height: "35px",
                    cursor: "default",
                  }}
                >
                  {!partidaInfo.checked && partsChecked.length === 0 && (
                    <div
                      className="inline"
                      style={{
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {renderDragHandle({})}
                    </div>
                  )}
                </div>
              </td>
            )
          }
        </tr>
      </StrikethroughRow>

      <ModalEditarPartida open={open} setOpen={setOpen} partida={partidaInfo} />
      {openEliminate && (
        <ModalDeletePart
          openEliminate={openEliminate}
          setOpenEliminate={setOpenEliminate}
          partida={partida}
        />
      )}
      {openDecompositionModal && (
        <ModalDecomposition
          open={openDecompositionModal}
          setOpen={setOpenDecompositionModal}
          partida={partidaInfo}
          functionToDo={manageDecomposition}
        />
      )}
      {
        <DialogCertificateMeasures
          open={isCertificateMeasuresOpen}
          partidaInfo={{
            ...partidaInfo,
            measures: partida.measures ?? partidaInfo?.measures ?? [],
          }}
          onClose={() => setIsCertificateMeasuresOpen(false)}
          onMeasuresUpdate={handleMeasuresUpdate}
          measureCertCheckboxChecked={measuresCertCheckboxChecked}
          onMeasureCertCheckboxChange={handleMeasureCertCheckboxChange}
          measureCertCheckboxDisabled={measureFooterCertCheckboxDisabled}
        />
      }
      <ModalCertAbove100
        open={isModalCertAbove100Open}
        isCertifying={isCertifying}
        certifyingAction={certifyingAction}
        partidaName={partida.name || partida.resumen}
        partidaRef={
          numeroIndex ? `${numeroIndex}.${partidaInfo.position + 1}` : null
        }
        onClose={() => {
          if (previousViewState) {
            setViewState(previousViewState)
          } else {
            const fieldName =
              pendingCertType === "totalCert"
                ? "cert_totalView"
                : pendingCertType === "totalPercentage"
                  ? "cert_totalPercView"
                  : pendingCertType === "currentCert"
                    ? "cert_currentView"
                    : pendingCertType === "currentPercentage"
                      ? "cert_currentPercView"
                      : null
            if (fieldName) {
              const newViewState = { ...viewState }
              delete newViewState[fieldName]
              setViewState(newViewState)
            }
          }
          setIsModalCertAbove100Open(false)
          setPendingCertValue(null)
          setPendingCertType(null)
          setPreviousViewState(null)

          if (sequentialModals && sequentialModals.hasCurrentModal()) {
            sequentialModals.resolveCurrent(null)
          }
        }}
        onCertifyWithValue={handleCertifyWithValue}
        onCertifyAt100={handleCertifyAt100}
        percentage={(() => {
          if (pendingCertValue === null || pendingCertType === null) return null
          const value = parseFloat(pendingCertValue)
          switch (pendingCertType) {
            case "totalCert":
              return (100 * value) / partida.quantity
            case "totalPercentage":
              return value
            case "currentCert":
              return (100 * (partida.cert_previous + value)) / partida.quantity
            case "currentPercentage":
              return (100 * partida.cert_previous) / partida.quantity + value
            default:
              return null
          }
        })()}
      />
      {indice === 0 && (
        <ModalCertChapterConfirm
          open={isModalCertChapterConfirmOpen}
          onClose={handleChapterCertCancel}
          onAccept={handleChapterCertAccept}
          certType={pendingChapterCertType}
        />
      )}
    </>
  )
}

Partida.propTypes = {
  partidaInfo: PropTypes.object.isRequired,
  indice: PropTypes.number.isRequired,
  soyFinal: PropTypes.bool,
  projectInfo: PropTypes.object.isRequired,
  modificado: PropTypes.number,
  setModificado: PropTypes.func,
  contadorActualizaciones: PropTypes.number,
  setContadorActualizaciones: PropTypes.func,
  ocultoFinal: PropTypes.bool,
  isChecked: PropTypes.bool,
  setIsChecked: PropTypes.func,
  version: PropTypes.object,
  numeroIndex: PropTypes.string,
  openModalCreatePartida: PropTypes.bool,
  setOpenModalCreatePartida: PropTypes.func,
  certificaciones: PropTypes.bool,
  currentPercentageChapter: PropTypes.number,
  setCurrentPercentageChapter: PropTypes.func,
  currentPercentagePartida: PropTypes.number,
  setParentUpdate: PropTypes.func,
  parentUpdate: PropTypes.object,
  isFirstTime: PropTypes.bool,
  isDraggingPartida: PropTypes.bool,
  budgetSearchFilterActive: PropTypes.bool,
  sequentialModals: PropTypes.object,
}

export default React.memo(Partida)
